import { z } from 'zod';
import { defineRoute } from '../core/contract';
import { DRILL_ATTEMPT_REJECT_CODES } from '../core/errors';
import { eventBatchResZ, MAX_EVENT_BATCH } from '../core/event-batch';
import { IdZ, qInt, qList, TsZ } from '../schemas/common';
import {
  CombineLevelZ,
  DrillAttemptZ,
  DrillProgramZ,
  DrillSessionDetailZ,
  DrillSessionZ,
  DrillShotClassZ,
  SpotKeyZ,
  StoredDrillAttemptZ,
} from '../schemas/drill';

/**
 * Owner: combine (server/src/modules/drills, client/src/modules/combine). The combine only
 * records attempts; ratings come from the one stats engine (R17).
 */

export const DrillLevelInfoZ = z.object({
  level: CombineLevelZ,
  name: z.string(),
  blurb: z.string(),
  core: z.boolean(),
  /** Shots per player, for example 25 for The Arc. null for Iso Gauntlet. */
  shots: z.number().int().positive().nullable(),
  plan: z.array(z.object({ spot: SpotKeyZ, shotClass: DrillShotClassZ, count: z.number().int().positive() })),
});

export const AttemptBatchReqZ = z.object({
  attempts: z.array(DrillAttemptZ).min(1).max(MAX_EVENT_BATCH),
});
export const AttemptBatchResZ = eventBatchResZ(DRILL_ATTEMPT_REJECT_CODES);

export const CombineStatusZ = z.object({
  players: z.array(
    z.object({
      playerId: IdZ,
      coreDone: z.boolean(),
      levels: z.record(
        CombineLevelZ,
        z.object({
          done: z.boolean(),
          makes: z.number().int(),
          attempts: z.number().int(),
          drillSessionId: IdZ.nullable(),
          at: TsZ.nullable(),
        }),
      ),
    }),
  ),
});

export const catalog = defineRoute({
  name: 'drills.catalog',
  method: 'GET',
  path: '/api/drills/catalog',
  auth: 'mem',
  res: z.object({ levels: z.array(DrillLevelInfoZ) }),
});

export const createSession = defineRoute({
  name: 'drills.createSession',
  method: 'POST',
  path: '/api/drills/sessions',
  auth: 'memw',
  body: z.object({
    id: IdZ,
    program: DrillProgramZ,
    level: CombineLevelZ.nullable(),
    playerIds: z.array(IdZ).min(1).max(16),
    startedAt: TsZ,
    clientTs: TsZ,
    /** undefined: auto. null: detached. */
    sessionId: IdZ.nullable().optional(),
  }),
  res: DrillSessionZ,
  status: 201,
  idempotent: true,
});

export const listSessions = defineRoute({
  name: 'drills.listSessions',
  method: 'GET',
  path: '/api/drills/sessions',
  auth: 'mem',
  query: z.object({
    playerId: IdZ.optional(),
    sessionId: IdZ.optional(),
    before: qInt(0, Number.MAX_SAFE_INTEGER).optional(),
    limit: qInt(1, 100).optional(),
  }),
  res: z.object({ items: z.array(DrillSessionZ), nextBefore: TsZ.nullable() }),
});

export const getSession = defineRoute({
  name: 'drills.getSession',
  method: 'GET',
  path: '/api/drills/sessions/:id',
  auth: 'mem',
  res: DrillSessionDetailZ,
  errors: ['NOT_FOUND'],
});

export const postAttempts = defineRoute({
  name: 'drills.postAttempts',
  method: 'POST',
  path: '/api/drills/sessions/:id/attempts',
  auth: 'mem',
  body: AttemptBatchReqZ,
  res: AttemptBatchResZ,
  maxBodyBytes: 256 * 1024,
  summary: 'Same response shape as game events (R4).',
});

export const pullAttempts = defineRoute({
  name: 'drills.pullAttempts',
  method: 'GET',
  path: '/api/drills/sessions/:id/attempts',
  auth: 'mem',
  query: z.object({ after: qInt(0, Number.MAX_SAFE_INTEGER).optional() }),
  res: z.object({ attempts: z.array(StoredDrillAttemptZ), serverSeq: z.number().int(), serverNow: TsZ }),
});

export const voidAttempt = defineRoute({
  name: 'drills.voidAttempt',
  method: 'POST',
  path: '/api/drills/attempts/:id/void',
  auth: 'mem',
  res: StoredDrillAttemptZ,
  errors: ['NOT_FOUND', 'FORBIDDEN'],
  summary: 'Recorder or owner. Audited.',
});

export const finishSession = defineRoute({
  name: 'drills.finishSession',
  method: 'POST',
  path: '/api/drills/sessions/:id/finish',
  auth: 'mem',
  body: z.object({ endedAt: TsZ, status: z.enum(['final', 'abandoned']).default('final') }),
  res: DrillSessionDetailZ,
  summary: 'Publishes drill.session.finished; stats recomputes and fills deltas.',
});

export const combineStatus = defineRoute({
  name: 'drills.combineStatus',
  method: 'GET',
  path: '/api/drills/combine',
  auth: 'mem',
  query: z.object({ playerIds: qList(IdZ, 40).optional() }),
  res: CombineStatusZ,
});
